import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceRole } from '@os/shared';
import { useApi } from '../../app/providers';
import type { GrantScope } from '../../lib/sharing-types';
import { withPage } from './share-url';
import { Button, CopyButton, Dialog, Tabs } from '../../ui';
import { LinksTab } from './LinksTab';
import { PeopleTab } from './PeopleTab';
import { ViewerCommentsToggle } from '../comments/ViewerCommentsToggle';
import { usePaletteCommands } from '../palette/registry';

/**
 * "Share" for managers (spec §9.2: org members); hidden in local mode. `bare` (phones, P5f): no button, the main
 * menu's Board › Share opens the dialog through the palette command.
 */
export function ShareButton({ scope, title, role, bare }: { scope: GrantScope; title: string; role: ResourceRole; bare?: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const allowed = role === 'manager' && api.capabilities.sharing;
  usePaletteCommands(() => (allowed ? [{ id: 'share', label: t('sharing.share'), group: 'board', shortcut: 'mod+shift+s', keywords: ['invite', 'inviter', 'partager', 'link', 'lien'], run: () => setOpen(true) }] : []));
  // Cmd/Ctrl+Shift+S (P16-3 Ruling 8): Share left the top bar, it stays two gestures away; not while typing.
  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey || e.code !== 'KeyS' || e.defaultPrevented) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target && (target.isContentEditable || target.matches('input, textarea, select'))) return;
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [allowed]);
  if (!allowed) return null;
  return (
    <>
      {!bare && (
        <Button onClick={() => setOpen(true)}>
          {t('sharing.share')}
        </Button>
      )}
      {open && <ShareDialog scope={scope} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}

/** People (invitations and grants) for a project or a board; secret links for a board only. */
/** `page`: the page open in the editor when the board has several (P14): its own link, and where a new secret link opens. */
export function ShareDialog({ scope, title, onClose, page }: { scope: GrantScope; title: string; onClose: () => void; page?: { id: string; name: string } }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'people' | 'link'>('people');
  const tabs = scope.type === 'board' ? (['people', 'link'] as const) : (['people'] as const);
  return (
    <Dialog title={t('sharing.title', { name: title })} onClose={onClose} size="md">
      {scope.type === 'board' && <ViewerCommentsToggle boardId={scope.id} />}
      {tabs.length > 1 ? (
        <Tabs.Root value={tab} onValueChange={(v) => setTab(v as 'people' | 'link')}>
          <Tabs.List className="mb-1">
            {tabs.map((k) => (
              <Tabs.Tab key={k} value={k}>
                {t(`sharing.${k}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel value="people">
            <PeopleTab scope={scope} />
          </Tabs.Panel>
          <Tabs.Panel value="link">
            <LinksTab boardId={scope.id} page={page} />
          </Tabs.Panel>
        </Tabs.Root>
      ) : (
        <PeopleTab scope={scope} />
      )}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {page && scope.type === 'board' && (
          <CopyButton variant="ghost" text={withPage(`${window.location.origin}/b/${scope.id}`, page.id)} copiedLabel={t('sharing.copied')} failedLabel={t('sharing.copyFailed')}>
            {t('sharing.copyPageLink', { name: page.name })}
          </CopyButton>
        )}
        <Button onClick={onClose}>
          {t('sharing.close')}
        </Button>
      </div>
    </Dialog>
  );
}
